import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { flattenToAppURL, useDetectClickOutside } from '@plone/volto/helpers';

import { Input } from '@designsystem/stories/02-atoms/Input';
import { Button } from '@designsystem/stories/02-atoms/Button';
import { ButtonGroup } from '@designsystem/stories/03-molecules/ButtonGroup';
import { Menu } from '@designsystem/stories/03-molecules/Menu';
import { SearchTabs } from '@fhnw/components/GlobalSearch/SearchTabs';
import { ResultItem } from '@fhnw/components/GlobalSearch/ResultItems';
import { solrSearchContent } from '@kitconcept/volto-solr/actions';
import { getSuggestions } from '@kitconcept/volto-solr/components/theme/SolrSearch/getSuggestions';
import { getCollationMisspellings } from '@kitconcept/volto-solr/components/theme/SolrSearch/getCollationMisspellings';
import { getNavigationWithExcluded } from '@kitconcept/volto-solr/actions';
import { getSearchnav } from '@fhnw/actions/searchnav/searchnav';
import { normalizeNavigation } from '@fhnw/helpers/DesignSystem/normalizeNavigation';

const messages = defineMessages({
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  close: {
    id: 'Close Global Search',
    defaultMessage: 'Close Global Search',
  },
  searchResults: {
    id: 'Search results',
    defaultMessage: 'Search results',
  },
  resultsFor: {
    id: 'Results for',
    defaultMessage: 'Results for',
  },
  noResultsFoundFor: {
    id: 'No results found for',
    defaultMessage: 'No results found for',
  },
  introStrong: {
    id: 'Type a search term',
    defaultMessage: 'Type a search term',
  },
  intro: {
    id: 'and search continuing education,degree programmes, events, documents and other content.',
    defaultMessage:
      'and search continuing education,degree programmes, events, documents and other content.',
  },
  continueEducation: {
    id: 'Continueeducation',
    defaultMessage: 'Search continuing education',
  },
  news: {
    id: 'Search news',
    defaultMessage: 'Search news',
  },
  degreeProgram: {
    id: 'Search degree program',
    defaultMessage: 'Search degree program',
  },
  event: {
    id: 'Search events',
    defaultMessage: 'Search events',
  },
  people: {
    id: 'Search people',
    defaultMessage: 'Search people',
  },
  suggestions: {
    id: 'Search suggestions',
    defaultMessage: 'Search suggestions',
  },
  noResult: {
    id: 'Your search request has no results',
    defaultMessage: 'Your search request has no results',
  },
  newSearch: {
    id: 'Please enter a new search request',
    defaultMessage: 'Please enter a new search request',
  },
  placeholder: {
    id: 'Search: type here',
    defaultMessage: 'Search: type here',
  },
  moreResults: {
    id: 'More results',
    defaultMessage: 'More results',
  },
  of: {
    id: 'of',
    defaultMessage: 'of',
  },
});

const emptyArray: any[] = [];

type GlobalSearchPanelProps = {
  closeGlobalSearch: () => void;
};

const GlobalSearchPanel = ({ closeGlobalSearch }: GlobalSearchPanelProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchWord, setSearchWord] = useState('');
  const [groupSelect, setGroupSelect] = useState(0);
  const searchNav = useSelector((state: any) => state.searchnav);
  const searchNavItems = searchNav?.items || emptyArray;

  const result = useSelector((state: any) => state.solrsearch);
  const suggestions = getSuggestions(
    result?.spellcheck?.suggestions || emptyArray,
  );

  const showSuggestions = suggestions?.length > 0;

  const totalResult = result?.total;
  const resultItems = result?.items || emptyArray;
  const showResultItems = resultItems.length > 0 && searchWord.length > 2;
  const totalResultItems = result?.items?.length;
  const b_size = 9;
  const collationMisspellings = getCollationMisspellings(
    result?.collationMisspellings,
  );
  const correctedSearchWord =
    showResultItems && collationMisspellings[searchWord] !== searchWord
      ? collationMisspellings[searchWord]
      : null;

  // Fetch information for search
  const has_navigation_with_excluded = useSelector(
    (state: any) =>
      state.navigation_with_excluded?.loaded ||
      state.navigation_with_excluded?.loading,
  );
  const folder = useSelector((state: any) => state.content?.data?.['@id']);

  const lang = useSelector((state: any) => state.intl.locale);
  // If the current path is not set, use the folder path
  const path = flattenToAppURL(folder) || '/' + lang;

  if (groupSelect > 0 && searchWord.length > 2 && totalResultItems === 0) {
    setGroupSelect(0);
  }

  useEffect(() => {
    if (path && !has_navigation_with_excluded) {
      dispatch(getNavigationWithExcluded(path, 3));
    }
  }, [dispatch, path, has_navigation_with_excluded]);
  // Fetch information for search
  const has_searchnav = searchNav?.loaded || searchNav?.loading;
  useEffect(() => {
    if (path && !has_searchnav) {
      dispatch(getSearchnav(path));
    }
  }, [dispatch, path, has_searchnav]);

  const onSolrSearch = useCallback(
    (searchWord: string, groupSelect: number, b_size: number) => {
      if (searchWord.length > 1) {
        dispatch(
          solrSearchContent('/', {
            SearchableText: searchWord,
            doEmptySearch: true,
            group_select: groupSelect,
            b_size,
            lang: intl.locale,
            collate: false,
          }),
        );
      }
    },
    [dispatch, intl.locale],
  );

  useEffect(() => {
    onSolrSearch(searchWord, groupSelect, b_size);
  }, [dispatch, groupSelect, searchWord, b_size, onSolrSearch]);

  const onMoreResult = () => {
    onSolrSearch(searchWord, groupSelect, totalResultItems + b_size);
  };

  const globalSearchPanelContainer = useDetectClickOutside({
    onTriggered: closeGlobalSearch,
    triggerKeys: ['Escape'],
    disableClick: true,
    disableKeys: undefined,
    allowAnyKey: undefined,
  });

  return (
    <div className="global-search" ref={globalSearchPanelContainer}>
      <div className="global-search__bar">
        <form
          autoComplete="off"
          id="searchbar"
          onSubmit={(e) => e.preventDefault()}
        >
          <label htmlFor="searchbar-input" className="visually-hidden">
            {intl.formatMessage(messages.search)}
          </label>
          <Input
            ref={inputRef}
            id="searchbar-input"
            type="text"
            size="large"
            placeholder={intl.formatMessage(messages.placeholder)}
            value={searchWord}
            onChange={(value: string) => setSearchWord(value)}
          />
        </form>
      </div>
      {!searchWord && (
        <div className="global-search__intro">
          <p>
            <strong>{intl.formatMessage(messages.introStrong)}</strong>{' '}
            {intl.formatMessage(messages.intro)}
          </p>
          {searchNavItems && (
            <Menu items={normalizeNavigation(searchNavItems)} />
          )}
        </div>
      )}
      {searchWord && (
        <div id="results" className="global-search__results">
          {(showResultItems || groupSelect > 0) && (
            <SearchTabs
              groupSelect={groupSelect}
              setGroupSelect={(groupSelect: number) =>
                setGroupSelect(groupSelect)
              }
              facetGroups={result?.facetGroups}
            />
          )}
          {correctedSearchWord && (
            <div className="global-search__corrected-title">
              <div className="global-search__corrected-title-line1">
                {intl.formatMessage(messages.resultsFor)}{' '}
                <strong>"{correctedSearchWord}"</strong>
              </div>
              <div className="global-search__corrected-title-line2">
                {intl.formatMessage(messages.noResultsFoundFor)}{' '}
                <strong>"{searchWord}"</strong>
              </div>
            </div>
          )}
          {showSuggestions && (
            <div className="global-search__suggestions">
              <h4 className="global-search__suggestions-title">
                {intl.formatMessage(messages.suggestions)}
              </h4>
              <p>
                <ButtonGroup
                  className="global-search__suggestions-container"
                  items={suggestions.map((suggestion: any, index: number) => ({
                    label: suggestion,
                    type: 'secondary',
                    onClick: () => setSearchWord(suggestion),
                  }))}
                />
              </p>
            </div>
          )}

          {showResultItems && (
            <>
              <h4 className="global-search__results-title">
                {intl.formatMessage(messages.searchResults)}
              </h4>
              <div>
                {resultItems.map((item: any, index: number) => (
                  <ResultItem
                    key={`${index}-${item['@id']}`}
                    item={item}
                    closeGlobalSearch={closeGlobalSearch}
                  />
                ))}
              </div>
              <div className="global-search__results-more">
                {totalResultItems >= totalResult ? null : (
                  <Button
                    onClick={onMoreResult}
                    label={
                      totalResultItems >= totalResult
                        ? ''
                        : intl.formatMessage(messages.moreResults)
                    }
                  />
                )}
                <span>{`${totalResultItems} ${intl.formatMessage(messages.of)} ${totalResult}`}</span>
              </div>
            </>
          )}

          {!showResultItems && searchWord.length > 2 && (
            <p className="global-search__noresult">
              <strong>{intl.formatMessage(messages.noResult)}</strong>
              <br />
              <span>{intl.formatMessage(messages.newSearch)}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchPanel;
